import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  Unsubscribe,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  Issue,
  IssueCategory,
  IssuePriority,
  IssueStatus,
  Comment,
  InternalNote,
  UserProfile,
  IssueActivity,
} from "../types";
import { calculateSlaDeadlines } from "../utils/slaCalculator";
import { gatherSourceMetadata } from "../utils/telemetry";

export interface CreateIssueInput {
  appId: string;
  appName: string;
  title: string;
  category: IssueCategory;
  priority: IssuePriority;
  description: string;
  expectedBehavior?: string | null;
  url?: string;
  attachmentPaths?: string[];
  ipAddress?: string | null;
}

/**
 * Formats status strings for user display in logs.
 */
function formatStatusLabel(status: IssueStatus): string {
  switch (status) {
    case "new":
      return "New";
    case "in_review":
      return "In Review";
    case "in_progress":
      return "In Progress";
    case "pending_user_feedback":
      return "Pending Feedback";
    case "resolved":
      return "Resolved";
    case "closed":
      return "Closed";
    default:
      return status;
  }
}

/**
 * Generates an issue ID, e.g. MLC-1042
 */
export function generateIssueId(): string {
  const randomSuffix = Math.floor(1000 + Math.random() * 9000);
  return `MLC-${randomSuffix}`;
}

/**
 * Creates a new issue document in mlc-bug-tracking-db with calculated SLA deadlines and activity audit.
 */
export async function createIssue(
  input: CreateIssueInput,
  reporter: UserProfile,
): Promise<Issue> {
  const issueId = generateIssueId();
  const now = new Date();
  const nowIso = now.toISOString();

  const sla = calculateSlaDeadlines(now, input.priority);
  const sourceMetadata = gatherSourceMetadata({
    url: input.url,
    ipAddress: input.ipAddress,
  });

  const newIssue: Issue = {
    id: issueId,
    appId: input.appId,
    appName: input.appName,
    title: input.title.trim(),
    category: input.category,
    priority: input.priority,
    status: "new",
    description: input.description.trim(),
    expectedBehavior: input.expectedBehavior
      ? input.expectedBehavior.trim()
      : null,
    reporterUid: reporter.uid,
    reporterEmail: reporter.email,
    reporterName: reporter.displayName,
    assigneeUid: null,
    assigneeEmail: null,
    sourceMetadata,
    attachmentPaths: input.attachmentPaths || [],
    sla,
    createdAt: nowIso,
    updatedAt: nowIso,
    resolvedAt: null,
    closedAt: null,
  };

  const issueDocRef = doc(db, "issues", issueId);
  await setDoc(issueDocRef, {
    ...newIssue,
    serverCreatedAt: serverTimestamp(),
    serverUpdatedAt: serverTimestamp(),
  });

  // Record initial creation activity in subcollection
  try {
    await recordActivity(issueId, {
      issueId,
      type: "created",
      actorUid: reporter.uid,
      actorName: reporter.displayName,
      actorEmail: reporter.email,
      actorRole: reporter.role,
      summary: `${reporter.displayName} created ticket`,
      timestamp: nowIso,
    });
  } catch (err: unknown) {
    console.warn("Could not record initial creation activity:", err);
  }

  return newIssue;
}

/**
 * Updates an issue status (e.g. In Progress, Resolved) and sets explicit resolvedAt/closedAt timestamps.
 */
export async function updateIssueStatus(
  issueId: string,
  newStatus: IssueStatus,
  actor?: UserProfile,
  previousStatus?: IssueStatus,
): Promise<void> {
  const issueRef = doc(db, "issues", issueId);
  const nowIso = new Date().toISOString();
  const updates: Record<string, unknown> = {
    status: newStatus,
    updatedAt: nowIso,
    serverUpdatedAt: serverTimestamp(),
  };
  if (actor) {
    updates.lastUpdatedByEmail = actor.email;
    updates.lastUpdatedByRole = actor.role;
  }

  if (newStatus === "resolved") {
    updates.resolvedAt = nowIso;
    updates.closedAt = null;
  } else if (newStatus === "closed") {
    updates.closedAt = nowIso;
    updates.resolvedAt = nowIso;
  } else {
    // Reopened or active: clear explicit resolution timestamps
    updates.resolvedAt = null;
    updates.closedAt = null;
  }

  await updateDoc(issueRef, updates);

  if (actor) {
    try {
      const fromLabel = previousStatus
        ? formatStatusLabel(previousStatus)
        : "Previous Status";
      const toLabel = formatStatusLabel(newStatus);
      await recordActivity(issueId, {
        issueId,
        type: "status_changed",
        actorUid: actor.uid,
        actorName: actor.displayName,
        actorEmail: actor.email,
        actorRole: actor.role,
        summary: `${actor.displayName} changed status from ${fromLabel} to ${toLabel}`,
        changes: {
          status: {
            from: previousStatus || "new",
            to: newStatus,
          },
        },
        timestamp: nowIso,
      });
    } catch (err: unknown) {
      console.warn("Could not record status activity:", err);
    }
  }
}

/**
 * Assigns an issue to a team member, updates SLA assigned timestamp, and logs activity.
 */
export async function assignIssue(
  issueId: string,
  assignee: { uid: string; email: string; displayName: string } | null,
  actor?: UserProfile,
  previousAssigneeEmail?: string | null,
): Promise<void> {
  const issueRef = doc(db, "issues", issueId);
  const nowIso = new Date().toISOString();

  await updateDoc(issueRef, {
    assigneeUid: assignee ? assignee.uid : null,
    assigneeEmail: assignee ? assignee.email : null,
    "sla.assignedAt": assignee ? nowIso : null,
    updatedAt: nowIso,
    serverUpdatedAt: serverTimestamp(),
    lastUpdatedByEmail: actor?.email ?? null,
    lastUpdatedByRole: actor?.role ?? "it_admin",
  });

  if (actor) {
    try {
      await recordActivity(issueId, {
        issueId,
        type: assignee ? "assigned" : "unassigned",
        actorUid: actor.uid,
        actorName: actor.displayName,
        actorEmail: actor.email,
        actorRole: actor.role,
        summary: assignee
          ? `${actor.displayName} assigned ticket to ${assignee.displayName}`
          : `${actor.displayName} unassigned ticket`,
        changes: {
          assignee: {
            from: {
              uid: null,
              email: previousAssigneeEmail ?? null,
              name: previousAssigneeEmail?.split("@")[0] ?? null,
            },
            to: assignee
              ? {
                  uid: assignee.uid,
                  email: assignee.email,
                  name: assignee.displayName,
                }
              : { uid: null, email: null, name: null },
          },
        },
        timestamp: nowIso,
      });
    } catch (err: unknown) {
      console.warn("Could not record assignment activity:", err);
    }
  }
}

/**
 * Deletes an issue document from mlc-bug-tracking-db.
 * Allowed by security rules for IT Administrators.
 */
export async function deleteIssue(issueId: string): Promise<void> {
  const issueRef = doc(db, "issues", issueId);
  await deleteDoc(issueRef);
}

/**
 * Bulk updates the status of multiple issues.
 */
export async function bulkUpdateIssueStatus(
  issueIds: string[],
  newStatus: IssueStatus,
  actor?: UserProfile,
  previousStatuses?: Map<string, IssueStatus>,
): Promise<void> {
  await Promise.all(
    issueIds.map((id) =>
      updateIssueStatus(
        id,
        newStatus,
        actor,
        previousStatuses ? previousStatuses.get(id) : undefined,
      ),
    ),
  );
}

/**
 * Bulk assigns multiple issues to an administrator (or unassigns if null).
 */
export async function bulkAssignIssues(
  issueIds: string[],
  assignee: { uid: string; email: string; displayName: string } | null,
  actor?: UserProfile,
  previousAssignees?: Map<string, string | null>,
): Promise<void> {
  await Promise.all(
    issueIds.map((id) =>
      assignIssue(
        id,
        assignee,
        actor,
        previousAssignees ? previousAssignees.get(id) : undefined,
      ),
    ),
  );
}

/**
 * Bulk deletes multiple issues.
 * Allowed by security rules for IT Administrators.
 */
export async function bulkDeleteIssues(issueIds: string[]): Promise<void> {
  await Promise.all(issueIds.map((id) => deleteIssue(id)));
}

/**
 * Subscribes to issues collection in realtime.
 */
export function subscribeToIssues(
  onUpdate: (issues: Issue[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const issuesCol = collection(db, "issues");
  return onSnapshot(
    issuesCol,
    (snap) => {
      const issueMap = new Map<string, Issue>();
      snap.forEach((d) => {
        const item = d.data() as Issue;
        const id = item.id || d.id;
        issueMap.set(id, { ...item, id });
      });
      const issues = Array.from(issueMap.values());
      // Sort newest first
      issues.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      onUpdate(issues);
    },
    (err) => {
      if (onError) onError(err);
    },
  );
}

/**
 * Adds a public discussion comment.
 */
export async function addComment(
  issueId: string,
  author: UserProfile,
  content: string,
  attachmentPaths: string[] = [],
): Promise<void> {
  const commentsCol = collection(db, "issues", issueId, "comments");
  const issueRef = doc(db, "issues", issueId);
  const nowIso = new Date().toISOString();

  await addDoc(commentsCol, {
    issueId,
    authorUid: author.uid,
    authorName: author.displayName,
    authorEmail: author.email,
    authorRole: author.role,
    content: content.trim(),
    attachmentPaths,
    createdAt: nowIso,
    serverCreatedAt: serverTimestamp(),
  });

  await updateDoc(issueRef, {
    updatedAt: nowIso,
    serverUpdatedAt: serverTimestamp(),
    lastCommentAuthorEmail: author.email,
    lastCommentAuthorRole: author.role,
    lastUpdatedByEmail: author.email,
    lastUpdatedByRole: author.role,
    lastCommentAt: nowIso,
  });
}

/**
 * Subscribes to public comments for an issue.
 */
export function subscribeToComments(
  issueId: string,
  onUpdate: (comments: Comment[]) => void,
): Unsubscribe {
  const commentsCol = collection(db, "issues", issueId, "comments");
  return onSnapshot(commentsCol, (snap) => {
    const comments: Comment[] = [];
    snap.forEach((d) => {
      comments.push({ id: d.id, ...(d.data() as Omit<Comment, "id">) });
    });
    comments.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    onUpdate(comments);
  });
}

/**
 * Adds an internal admin-only note.
 */
export async function addInternalNote(
  issueId: string,
  author: UserProfile,
  content: string,
): Promise<void> {
  const notesCol = collection(db, "issues", issueId, "internal_notes");
  const nowIso = new Date().toISOString();

  await addDoc(notesCol, {
    issueId,
    authorUid: author.uid,
    authorName: author.displayName,
    content: content.trim(),
    createdAt: nowIso,
    serverCreatedAt: serverTimestamp(),
  });
}

/**
 * Subscribes to internal admin-only notes.
 */
export function subscribeToInternalNotes(
  issueId: string,
  onUpdate: (notes: InternalNote[]) => void,
): Unsubscribe {
  const notesCol = collection(db, "issues", issueId, "internal_notes");
  return onSnapshot(notesCol, (snap) => {
    const notes: InternalNote[] = [];
    snap.forEach((d) => {
      notes.push({ id: d.id, ...(d.data() as Omit<InternalNote, "id">) });
    });
    notes.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    onUpdate(notes);
  });
}

/**
 * Records an activity event in the issue's activities subcollection (audit trail).
 */
export async function recordActivity(
  issueId: string,
  activity: Omit<IssueActivity, "id">,
): Promise<void> {
  const activitiesCol = collection(db, "issues", issueId, "activities");
  await addDoc(activitiesCol, {
    ...activity,
    serverCreatedAt: serverTimestamp(),
  });
}

/**
 * Subscribes to realtime activity log (audit trail) for an issue.
 */
export function subscribeToActivities(
  issueId: string,
  onUpdate: (activities: IssueActivity[]) => void,
): Unsubscribe {
  const activitiesCol = collection(db, "issues", issueId, "activities");
  return onSnapshot(activitiesCol, (snap) => {
    const activities: IssueActivity[] = [];
    snap.forEach((d) => {
      activities.push({ id: d.id, ...(d.data() as Omit<IssueActivity, "id">) });
    });
    // Sort newest first for chronological feed display
    activities.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
    onUpdate(activities);
  });
}
